import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContextBackend'
import { useAccountLogo } from '../hooks/useAccountLogo'
import { uploadAccountLogo, deleteAccountLogo } from '../api/accountLogo'
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@t-one-internal/design-system'
import { Upload, Trash, X } from '@phosphor-icons/react'
import { LogoEditor } from './LogoEditor'
import { compressImage } from '../utils/imageCompression'

interface AccountLogoManagerProps {
  onLogoChange?: (logoUrl: string | null) => void
}

export const AccountLogoManager: React.FC<AccountLogoManagerProps> = ({
  onLogoChange,
}) => {
  const { isAuthenticated, account } = useAuth()
  
  const accountUuid = account?.account_uuid || ''
  const { logoUrl, isLoading, error, refetch, clearCache } = useAccountLogo(isAuthenticated)
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [selectedFormat, setSelectedFormat] = useState<'square' | 'rectangle'>('square')
  const [showEditor, setShowEditor] = useState(false)
  const [editorImageSrc, setEditorImageSrc] = useState<string | null>(null)
  const [croppedFile, setCroppedFile] = useState<File | null>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Create preview URL when file is selected
  useEffect(() => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile)
      setPreviewUrl(url)
      return () => URL.revokeObjectURL(url)
    } else {
      setPreviewUrl(null)
    }
  }, [selectedFile])

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setUploadError('Please select an image file')
        return
      }

      setUploadError(null)
      setDeleteError(null)

      try {
        // Compress the image before showing in editor to reduce memory usage
        // Use larger max size for initial compression (will be further compressed in editor)
        const compressedFile = await compressImage(file, 2000, 2000, 0.85)

        // Detect image aspect ratio to auto-select format
        const imageUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result as string)
          reader.readAsDataURL(compressedFile)
        })

        // Get image dimensions to determine aspect ratio
        const img = new Image()
        await new Promise<void>((resolve) => {
          img.onload = () => resolve()
          img.src = imageUrl
        })

        const aspectRatio = img.width / img.height
        // If image is wider than 1.5:1 (landscape), default to rectangle
        // Otherwise default to square
        const detectedFormat: 'square' | 'rectangle' = aspectRatio > 1.5 ? 'rectangle' : 'square'

        // Show editor with auto-detected format
        setSelectedFile(compressedFile)
        setSelectedFormat(detectedFormat)
        setEditorImageSrc(imageUrl)
        setShowEditor(true)
      } catch (err) {
        console.error('Error compressing image:', err)
        setUploadError('Failed to process image. Please try again.')
      }
    }
  }

  const handleFormatChange = (format: 'square' | 'rectangle') => {
    setSelectedFormat(format)
  }

  const handleEditorSave = async (croppedBlob: Blob) => {
    try {
      // Convert blob to File
      const croppedFile = new File([croppedBlob], 'logo.jpg', {
        type: 'image/jpeg',
        lastModified: Date.now()
      })

      // Store the cropped file
      setCroppedFile(croppedFile)

      // Create preview URL
      const previewUrl = URL.createObjectURL(croppedFile)
      setPreviewUrl(previewUrl)
      setShowEditor(false)

      // Replace the file input with cropped file
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(croppedFile)
      if (fileInputRef.current) {
        fileInputRef.current.files = dataTransfer.files
      }
    } catch (err) {
      console.error('Error saving cropped image:', err)
      setUploadError('Failed to save cropped image. Please try again.')
    }
  }

  const handleEditorCancel = () => {
    setShowEditor(false)
    setEditorImageSrc(null)
    setSelectedFormat('square')
    setSelectedFile(null)
    setPreviewUrl(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleUpload = async () => {
    // Use cropped file if available, otherwise use selected file
    const file = croppedFile || selectedFile
    if (!file) {
      setUploadError('Please select a file')
      return
    }

    setUploading(true)
    setUploadError(null)

    if (!accountUuid) {
      setUploadError('Account UUID not available. Cannot upload logo.')
      return
    }

    try {
      const response = await uploadAccountLogo(file, accountUuid)

      if (!response.success) {
        throw new Error(response.error || 'Upload failed')
      }

      // Update sessionStorage with new logo URL so sidebar updates immediately
      if (response.data?.logo) {
        sessionStorage.setItem('account_logo', response.data.logo)
      } else {
        sessionStorage.removeItem('account_logo')
      }

      // Clear cache and refetch
      clearCache()
      await refetch()

      // Notify parent component
      if (onLogoChange && response.data?.logo) {
        onLogoChange(response.data.logo)
      }

      // Trigger a custom event to notify other components (like sidebar) to refetch
      window.dispatchEvent(new CustomEvent('accountLogoUpdated', { 
        detail: { logoUrl: response.data?.logo || null } 
      }))

      // Reset everything
      setSelectedFile(null)
      setPreviewUrl(null)
      setCroppedFile(null)
      setSelectedFormat('square')
      setShowEditor(false)
      setEditorImageSrc(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload logo'
      setUploadError(errorMessage)
      console.error('Upload error:', err)
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async () => {
    setShowDeleteDialog(false)
    setDeleting(true)
    setDeleteError(null)

    if (!accountUuid) {
      setDeleteError('Account UUID not available. Cannot delete logo.')
      return
    }

    try {
      const response = await deleteAccountLogo(accountUuid)

      if (!response.success) {
        throw new Error(response.error || 'Delete failed')
      }

      // Clear sessionStorage so sidebar updates immediately
      sessionStorage.removeItem('account_logo')

      // Clear cache and refetch
      clearCache()
      await refetch()

      // Notify parent component
      if (onLogoChange) {
        onLogoChange(null)
      }

      // Trigger a custom event to notify other components (like sidebar) to refetch
      window.dispatchEvent(new CustomEvent('accountLogoUpdated', { 
        detail: { logoUrl: null } 
      }))
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete logo'
      setDeleteError(errorMessage)
      console.error('Delete error:', err)
    } finally {
      setDeleting(false)
    }
  }

  const handleCancelPreview = () => {
    setSelectedFile(null)
    setPreviewUrl(null)
    setCroppedFile(null)
    setSelectedFormat('square')
    setShowEditor(false)
    setEditorImageSrc(null)
    setUploadError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const getAccountName = () => {
    return account?.name || 'Company Name'
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Current Logo Display */}
      <div className="flex items-center gap-4">
        {logoUrl ? (
          <div className="relative">
            <img
              src={logoUrl}
              alt="Account Logo"
              className="w-32 h-32 object-contain rounded-lg border border-gray-200"
            />
            <button
              onClick={() => setShowDeleteDialog(true)}
              disabled={deleting}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors disabled:opacity-50"
              title="Delete logo"
            >
              <Trash className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
            <span className="text-gray-400 text-sm">No logo</span>
          </div>
        )}
      </div>

      {/* Error Messages */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error.message || 'Failed to load logo'}
        </div>
      )}
      {uploadError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {uploadError}
        </div>
      )}
      {deleteError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {deleteError}
        </div>
      )}

      {/* Logo Editor */}
      {showEditor && editorImageSrc && (
        <div className="space-y-3 p-4 border border-gray-200 rounded-lg bg-white">
          <LogoEditor
            imageSrc={editorImageSrc}
            onSave={handleEditorSave}
            onCancel={handleEditorCancel}
            format={selectedFormat}
            onFormatChange={handleFormatChange}
            companyName={getAccountName()}
          />
        </div>
      )}

      {/* Upload Controls */}
      <div className="space-y-3">
        {/* File Preview (after cropping) */}
        {previewUrl && !showEditor && (
          <div className="relative p-4 border border-gray-200 rounded-lg bg-gray-50">
            <p className="text-sm font-medium text-gray-700 mb-3">Preview:</p>
            {selectedFormat === 'square' ? (
              // Square preview with company name
              <div className="flex items-center gap-3 px-4 py-6 bg-white rounded-lg border border-gray-200" style={{ width: '256px' }}>
                <img
                  src={previewUrl}
                  alt="Logo Preview"
                  className="w-8 h-8 rounded-lg object-cover flex-shrink-0"
                />
                <span
                  className="text-xl font-semibold text-gray-900"
                  style={{
                    lineHeight: '1.3',
                    wordBreak: 'break-word',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    flex: 1,
                  }}
                >
                  {getAccountName()}
                </span>
              </div>
            ) : (
              // Rectangle preview (no text)
              <div className="flex items-center justify-center bg-white rounded-lg border border-gray-200 p-4" style={{ width: '256px' }}>
                <img
                  src={previewUrl}
                  alt="Logo Preview"
                  className="w-full h-auto rounded-lg object-contain"
                  style={{ maxHeight: '80px' }}
                />
              </div>
            )}
            <button
              onClick={handleCancelPreview}
              className="absolute top-6 right-6 bg-gray-800 text-white rounded-full p-1 hover:bg-gray-900 transition-colors"
              title="Cancel"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* File Input and Upload Button Container - Sticky when cropped file exists */}
        {croppedFile && (
          <div className="sticky top-4 z-20 bg-white p-3 border border-gray-200 rounded-lg shadow-sm">
            <div className="flex items-center gap-2">
              <div className="flex-1 text-sm text-gray-600">
                Photo ready to upload
              </div>
              <Button
                onClick={handleUpload}
                disabled={uploading || deleting}
                className="flex items-center gap-2 flex-shrink-0"
              >
                <Upload className="w-4 h-4" />
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
              <Button
                variant="outline"
                onClick={handleCancelPreview}
                disabled={uploading || deleting}
                className="flex-shrink-0"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* File Input - Always visible when no cropped file */}
        {!croppedFile && (
          <div className="flex items-center gap-2">
            <label className="flex-1">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                disabled={uploading || deleting || showEditor}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </label>
          </div>
        )}
        
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Logo</DialogTitle>
            <DialogDescription className="text-gray-600">
              Are you sure you want to delete this logo? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default AccountLogoManager

